import React, { useEffect, useRef, useState } from 'react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { confirmDialog, promptDialog } from '../../../lib/confirmStore';
import StageFocusSection from './StageFocusSection';
import { pickNamePart } from '../../../lib/nameUtils';
import type { ApplicantFull, FullApplicantRecord } from './types';

// Lazy imports for the heavy existing components (kept out of the initial bundle).
const ScreeningTab = React.lazy(() => import('../../admin/ScreeningTab'));

interface StageFocusContentProps {
  record: FullApplicantRecord;
  data: ApplicantFull;
  onChanged: () => void;
  onReload: () => void;
}

/**
 * Stage-focus content (refined spec 2026-10-11).
 *
 * Renders the FULLY FUNCTIONAL section for the current stage, highlighted as
 * the current-stage focus. Reuses existing components where they exist
 * (ScreeningTab); elsewhere wires the same backend endpoints the admin UI
 * uses. The admin does the work right here — no read-only summaries.
 */
export default function StageFocusContent({ record, data, onChanged, onReload }: StageFocusContentProps) {
  const stage = record.stage;
  const app = data.application;

  // FORENSIC FIX (2026-10-12): /full can return application: null when the
  // workflow record's application link cannot be resolved. Every focus
  // component below dereferences `app` unconditionally, so a null application
  // crashed the entire drawer body (blank panel). Render an explicit notice
  // instead — the gate panel, admin actions and sections above still work.
  if (!app) {
    return (
      <StageFocusSection stage={stage} title="Application document">
        <p className="text-xs text-red-600 font-medium">
          The application document for this applicant could not be loaded, so the stage tools are unavailable.
          Try <span className="font-bold">Recompute</span> in the drawer footer, or open the full profile.
        </p>
      </StageFocusSection>
    );
  }

  switch (stage) {
    case 'draft':
      return (
        <StageFocusSection stage={stage} title="Submission status">
          <DraftFocus app={app} onChanged={onChanged} onReload={onReload} />
        </StageFocusSection>
      );
    case 'submitted':
      return (
        <StageFocusSection stage={stage} title="Pre-vetting evidence">
          <SubmittedFocus record={record} data={data} />
        </StageFocusSection>
      );
    case 'readyForScreening':
      return (
        <ScreeningFocus app={app} onChanged={onChanged} onReload={onReload} />
      );
    case 'readyForProcessing':
      return (
        <StageFocusSection stage={stage} title="Placement management">
          <PlacementFocus app={app} onChanged={onChanged} onReload={onReload} />
        </StageFocusSection>
      );
    case 'billings':
      return (
        <StageFocusSection stage={stage} title="Finance — invoices & payments">
          <BillingsFocus data={data} onChanged={onChanged} onReload={onReload} />
        </StageFocusSection>
      );
    case 'immigration':
      return (
        <StageFocusSection stage={stage} title="Visa & immigration">
          <ImmigrationFocus app={app} onChanged={onChanged} onReload={onReload} />
        </StageFocusSection>
      );
    case 'preDeparture':
      return (
        <StageFocusSection stage={stage} title="Flight & arrival details">
          <PreDepartureFocus app={app} onChanged={onChanged} onReload={onReload} />
        </StageFocusSection>
      );
    case 'closed':
      return (
        <StageFocusSection stage={stage} title="Closure">
          <ClosedFocus record={record} app={app} onChanged={onChanged} />
        </StageFocusSection>
      );
    default:
      return null;
  }
}

/* ---------------- Ready for Screening: embedded screening tools ---------------- */

/**
 * FIX (2026-10-11): reloading-glitch root cause.
 *
 * ScreeningTab fires onRecordsChange on EVERY successful fetch — including its
 * initial mount fetch. The old code forwarded that straight into
 * onChanged()+onReload(), so: ScreeningTab mounts → fetch completes →
 * drawer reloads (full skeleton, content unmounts) → ScreeningTab remounts →
 * fetch completes → drawer reloads → … an infinite remount/fetch loop with a
 * flashing skeleton ("ugly reloading glitch").
 *
 * Fix: skip the first onRecordsChange per applicant (the initial load — the
 * drawer already holds fresh data). Genuine later changes (admin adds/edits a
 * screening record → ScreeningTab refetches internally WITHOUT unmounting)
 * still propagate, so the gate panel recomputes correctly.
 */
function ScreeningFocus({ app, onChanged, onReload }: { app: any; onChanged: () => void; onReload: () => void }) {
  const initialLoadDone = useRef(false);
  useEffect(() => {
    initialLoadDone.current = false;
  }, [app.applicationId]);

  return (
    <StageFocusSection stage="readyForScreening" title="Screening management">
      <React.Suspense fallback={<div className="text-xs text-slate-500 py-4 text-center">Loading screening tools…</div>}>
        <ScreeningTab
          applicationId={app.applicationId}
          isEnhancedReferrer={false}
          onRecordsChange={() => {
            if (!initialLoadDone.current) {
              initialLoadDone.current = true;
              return; // initial load — nothing to propagate
            }
            onChanged();
            onReload();
          }}
        />
      </React.Suspense>
    </StageFocusSection>
  );
}

/* ---------------- Draft: submission status ---------------- */

function DraftFocus({ app, onChanged, onReload }: { app: any; onChanged: () => void; onReload: () => void }) {
  const [busy, setBusy] = useState(false);
  const status = app.submissionStatus || app.status || 'Draft';
  const targetId = app.id || app.applicationId;

  const submitOnBehalf = async () => {
    // PR-16: firstName may be an encrypted PII block — sanitize so the
    // confirm dialog never reads "on behalf of [object Object]".
    const applicantName = pickNamePart(app.personalDetails?.firstName);
    const who = applicantName ? `on behalf of ${applicantName}` : 'on behalf of the applicant';
    if (!(await confirmDialog(`Submit application #${targetId} ${who}? This will send the confirmation email and notify the employer/referrer.`))) return;
    setBusy(true);
    try {
      await safeFetch(`/api/admin/applications/${encodeURIComponent(targetId)}/submit-on-behalf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      notifyToast('success', 'Application submitted. Confirmation email sent.');
      onChanged(); onReload();
    } catch (err: any) {
      notifyToast('error', err.message || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-slate-600">Waiting for submission.</p>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-bold text-slate-500 uppercase tracking-wide">Current status:</span>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-bold text-slate-700">{status}</span>
      </div>
      <button
        type="button"
        onClick={submitOnBehalf}
        disabled={busy}
        className="rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? 'Submitting…' : 'Submit on behalf of applicant'}
      </button>
    </div>
  );
}

/* ---------------- Submitted: evidence links ---------------- */

function SubmittedFocus({ record, data }: { record: FullApplicantRecord; data: ApplicantFull }) {
  const checks = record.preApprovalChecks || [];
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        The pre-vetting checklist above is the gate. Underlying evidence:
      </p>
      <ul className="space-y-1.5">
        {checks.map((c) => (
          <li key={c.code} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <span className="font-semibold text-slate-700">{c.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${
              c.state === 'pass' ? 'bg-emerald-100 text-emerald-700' :
              c.state === 'review' ? 'bg-red-100 text-red-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {c.state.replace('_', ' ')}
            </span>
          </li>
        ))}
      </ul>
      {data.application?.criminalDeclarationAnswered && (
        <p className="text-xs text-slate-500">Criminal disclosure answered — review notes in the application profile.</p>
      )}
    </div>
  );
}

/* ---------------- Ready for Processing: placements ---------------- */

function PlacementFocus({ app, onChanged, onReload }: { app: any; onChanged: () => void; onReload: () => void }) {
  // Ensure app object passed down to AdminPlacementsPanel always carries the
  // canonical doc ID as both `id` and `applicationId`.
  const canonicalApp = React.useMemo(() => {
    const targetId = app.id || app.applicationId;
    return { ...app, id: targetId, applicationId: targetId };
  }, [app]);

  // Minimal adapter: AdminPlacementsPanel expects appCtx with optional setters.
  const appCtx = React.useMemo(() => ({
    setSelectedApp: () => { onReload(); },
    setApplications: () => { onChanged(); },
  }), [onChanged, onReload]);

  const Panel = React.useMemo(() => React.lazy(() =>
    import('../../admin/ApplicantDetail').then((m) => ({ default: m.AdminPlacementsPanel }))
  ), []);

  return (
    <React.Suspense fallback={<div className="text-xs text-slate-500 py-4 text-center">Loading placement tools…</div>}>
      <Panel app={canonicalApp} appCtx={appCtx} canEdit={true} />
    </React.Suspense>
  );
}

/* ---------------- Billings: invoices & payments ---------------- */

function BillingsFocus({ data, onChanged, onReload }: { data: ApplicantFull; onChanged: () => void; onReload: () => void }) {
  const invoices = data.invoices || [];
  const [busy, setBusy] = useState(false);
  const targetAppId = data.application?.id || data.application?.applicationId;

  const recordPayment = async (invoiceId: string) => {
    const amountStr = await promptDialog('Payment amount (USD):');
    if (!amountStr) return;
    const amount = parseFloat(amountStr);
    if (!amount || amount <= 0) { notifyToast('error', 'Enter a valid amount'); return; }
    setBusy(true);
    try {
      await safeFetch('/api/admin/finance/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicantId: targetAppId,
          applicantUserId: data.record?.uid || '',
          amount,
          allocations: [{ invoiceId, amount }],
        }),
      });
      notifyToast('success', 'Payment recorded.');
      onChanged(); onReload();
    } catch (err: any) {
      notifyToast('error', err.message || 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {invoices.length === 0 && (
        <p className="text-xs text-slate-500">No invoices on file. Create one from the Finance section of the applicant profile.</p>
      )}
      {invoices.map((inv) => {
        // Canonical paid field on invoice docs is `amountPaid` (creation +
        // payment allocation); `paidAmount` is a legacy alias some docs carry.
        const paid = Number(inv.paidAmount ?? inv.amountPaid ?? 0);
        const total = Number(inv.total || 0);
        const due = Math.max(0, total - paid);
        const isPaid = due <= 0 && total > 0 || inv.status === 'Fully Paid' || inv.status === 'Paid';
        return (
          <div key={inv.id} className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[13px] font-bold text-slate-800">Invoice {inv.id.slice(0, 8)}</p>
                <p className="text-xs text-slate-500">
                  Total ${total.toFixed(2)} · Paid ${paid.toFixed(2)} · Due ${due.toFixed(2)}
                </p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${
                isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {isPaid ? 'Fully Paid' : inv.status}
              </span>
            </div>
            {due > 0 && (
              <button
                type="button"
                onClick={() => recordPayment(inv.id)}
                disabled={busy}
                className="mt-2 rounded-lg bg-success px-3 py-1.5 text-xs font-bold text-white hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50"
              >
                Record payment
              </button>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-slate-500">
        Full invoice builder, quotes, and payment→invoice allocation live in the applicant profile Finance tab.
      </p>
    </div>
  );
}

/* ---------------- Immigration: visa management ---------------- */

function ImmigrationFocus({ app, onChanged, onReload }: { app: any; onChanged: () => void; onReload: () => void }) {
  const [busy, setBusy] = useState(false);
  const targetId = app.id || app.applicationId;
  const vd = app.visaDetails || {};
  const [visaNo, setVisaNo] = useState(vd.visaNumber || app.visaNumber || '');
  const [visaStatus, setVisaStatus] = useState(vd.visaStatus || vd.visaApprovalStatus || app.visaStatus || 'Pending');
  const rawEmbassyDate = vd.embassyAppointmentAt || vd.embassyAppointmentDate || vd.appointmentDate || app.embassyAppointmentAt;
  const [embassyDate, setEmbassyDate] = useState(rawEmbassyDate ? String(rawEmbassyDate).slice(0, 16) : '');
  const [embassyLoc, setEmbassyLoc] = useState(vd.embassyLocation || app.embassyLocation || '');

  useEffect(() => {
    const v = app.visaDetails || {};
    setVisaNo(v.visaNumber || app.visaNumber || '');
    setVisaStatus(v.visaStatus || v.visaApprovalStatus || app.visaStatus || 'Pending');
    const rawD = v.embassyAppointmentAt || v.embassyAppointmentDate || v.appointmentDate || app.embassyAppointmentAt;
    setEmbassyDate(rawD ? String(rawD).slice(0, 16) : '');
    setEmbassyLoc(v.embassyLocation || app.embassyLocation || '');
  }, [app]);

  const save = async () => {
    setBusy(true);
    try {
      await safeFetch(`/api/admin/applications/${encodeURIComponent(targetId)}/visa`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visaNumber: visaNo.trim(),
          visaStatus,
          embassyAppointmentAt: embassyDate || null,
          embassyLocation: embassyLoc.trim(),
        }),
      });
      notifyToast('success', 'Visa details saved.');
      onChanged(); onReload();
    } catch (err: any) {
      notifyToast('error', err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:bg-white focus:border-blue-500";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Visa number</label>
          <input value={visaNo} onChange={(e) => setVisaNo(e.target.value)} className={inputCls} placeholder="e.g. J1234567" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Visa status</label>
          <select value={visaStatus} onChange={(e) => setVisaStatus(e.target.value)} className={inputCls}>
            <option>Pending</option>
            <option>Approved</option>
            <option>Issued</option>
            <option>Denied</option>
            <option>Rejected</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Embassy appointment</label>
          <input type="datetime-local" value={embassyDate} onChange={(e) => setEmbassyDate(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Embassy location</label>
          <input value={embassyLoc} onChange={(e) => setEmbassyLoc(e.target.value)} className={inputCls} placeholder="e.g. Kingston" />
        </div>
      </div>
      {visaStatus === 'Denied' || visaStatus === 'Rejected' ? (
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
          Refused — the application stays at Immigration. Update the details above and retry when ready.
        </p>
      ) : null}
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save visa details'}
      </button>
    </div>
  );
}

/* ---------------- Pre-Departure: flight & arrival ---------------- */

function PreDepartureFocus({ app, onChanged, onReload }: { app: any; onChanged: () => void; onReload: () => void }) {
  const fd = app.flightDetails || app.itinerary || {};
  const [busy, setBusy] = useState(false);
  const targetId = app.id || app.applicationId;
  const [flightNumber, setFlightNumber] = useState(fd.flightNumber || '');
  const [airline, setAirline] = useState(fd.airline || '');
  const [depAirport, setDepAirport] = useState(fd.departureAirport || '');
  const [arrAirport, setArrAirport] = useState(fd.arrivalAirport || '');
  const [depAt, setDepAt] = useState(fd.departureTime ? String(fd.departureTime).slice(0, 16) : '');
  const [arrAt, setArrAt] = useState(fd.arrivalTime ? String(fd.arrivalTime).slice(0, 16) : '');
  const [notes, setNotes] = useState(fd.notes || '');

  useEffect(() => {
    const f = app.flightDetails || app.itinerary || {};
    setFlightNumber(f.flightNumber || '');
    setAirline(f.airline || '');
    setDepAirport(f.departureAirport || '');
    setArrAirport(f.arrivalAirport || '');
    setDepAt(f.departureTime ? String(f.departureTime).slice(0, 16) : '');
    setArrAt(f.arrivalTime ? String(f.arrivalTime).slice(0, 16) : '');
    setNotes(f.notes || '');
  }, [app]);

  const save = async () => {
    setBusy(true);
    try {
      await safeFetch(`/api/applications/update-itinerary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationId: targetId,
          flightDetails: {
            flightNumber: flightNumber.trim(),
            airline: airline.trim(),
            departureAirport: depAirport.trim(),
            arrivalAirport: arrAirport.trim(),
            departureTime: depAt || null,
            arrivalTime: arrAt || null,
            notes: notes.trim(),
          },
        }),
      });
      notifyToast('success', 'Flight details saved.');
      onChanged(); onReload();
    } catch (err: any) {
      notifyToast('error', err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:bg-white focus:border-blue-500";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Airline</label>
          <input value={airline} onChange={(e) => setAirline(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Flight number</label>
          <input value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} className={inputCls} placeholder="e.g. AA1234" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Departure airport</label>
          <input value={depAirport} onChange={(e) => setDepAirport(e.target.value)} className={inputCls} placeholder="e.g. KIN" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Arrival airport</label>
          <input value={arrAirport} onChange={(e) => setArrAirport(e.target.value)} className={inputCls} placeholder="e.g. JFK" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Departure</label>
          <input type="datetime-local" value={depAt} onChange={(e) => setDepAt(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Arrival</label>
          <input type="datetime-local" value={arrAt} onChange={(e) => setArrAt(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Notes / arrival contacts</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} />
      </div>
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save flight details'}
      </button>
    </div>
  );
}

/* ---------------- Closed: closure + archive ---------------- */

function ClosedFocus({ record, app, onChanged }: { record: FullApplicantRecord; app: any; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const closureType = record.closureType || (app as any).closureType || null;
  const closureReason = (app as any).closureReason || '';
  const closedAt = (app as any).closedAt || '';
  const targetId = app.id || app.applicationId;

  const archive = async () => {
    if (!(await confirmDialog(`Archive the application for ${record.displayName}? It will move to Archived Applications.`))) return;
    setBusy(true);
    try {
      await safeFetch('/api/admin/applications/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationIds: [targetId] }),
      });
      notifyToast('success', 'Application archived.');
      onChanged();
    } catch (err: any) {
      notifyToast('error', err.message || 'Archive failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <dt className="font-bold uppercase tracking-wide text-slate-400 text-[10px]">Closure type</dt>
          <dd className="font-extrabold text-slate-800">{closureType || '—'}</dd>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <dt className="font-bold uppercase tracking-wide text-slate-400 text-[10px]">Closed at</dt>
          <dd className="font-semibold text-slate-800">{closedAt ? new Date(closedAt).toLocaleDateString() : '—'}</dd>
        </div>
      </dl>
      {closureReason && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span className="font-bold text-slate-500">Reason: </span>{closureReason}
        </p>
      )}
      <button
        type="button"
        onClick={archive}
        disabled={busy}
        className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {busy ? 'Archiving…' : 'Archive application'}
      </button>
    </div>
  );
}
